import Colors from '@constants/Colors';
import { Camera02Icon } from '@hugeicons/core-free-icons';
import { useFocusEffect } from '@react-navigation/native';
import {
  Button,
  ButtonVariants,
  COMMUNITY_APP_URL,
  IconButton,
  TextReg,
  useDynamicStyles,
  useSnackbar,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import {
  CameraView,
  useCameraPermissions,
  type ScanningResult,
} from 'expo-camera';
import * as Linking from 'expo-linking';
import { router } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';

export function QrScanner() {
  const { width: screenWidth } = useWindowDimensions();
  const styles = useDynamicStyles(createStyles, { screenWidth });
  const { showSnackbar } = useSnackbar();
  const [cameraFacing, setCameraFacing] = useState<'front' | 'back'>('back');
  const [permission, requestPermission] = useCameraPermissions();
  const [hasScanned, setHasScanned] = useState(false);

  // Extract hostname and app scheme from config for URL validation
  const { appHostname, appScheme } = useMemo(() => {
    const hostname = COMMUNITY_APP_URL.replace(/^https?:\/\//, '').split(
      '/',
    )[0];
    // Get app scheme from expo-linking (reads from app.json scheme)
    const schemeUrl = Linking.createURL('');
    const scheme = schemeUrl.split('://')[0];
    return { appHostname: hostname, appScheme: scheme };
  }, []);

  const toggleCamera = useCallback(() => {
    setCameraFacing((prev) => (prev === 'back' ? 'front' : 'back'));
  }, []);

  const extractUserIdFromUrl = useCallback(
    (url: string): string | null => {
      try {
        // Handle both https://{domain}/to/{userId} and {scheme}://to/{userId}
        if (url.includes(appHostname) || url.startsWith(`${appScheme}://`)) {
          const match = url.match(/\/to\/([^/?]+)/);
          return match?.[1] ?? null;
        }
        return null;
      } catch {
        return null;
      }
    },
    [appHostname, appScheme],
  );

  const onBarcodeScanned = useCallback(
    (scanningResult: ScanningResult) => {
      if (hasScanned) return;

      const { type, data } = scanningResult;
      if (type === 'qr') {
        const userId = extractUserIdFromUrl(data);
        if (userId) {
          setHasScanned(true);
          router.navigate(`/to/${userId}`);
        } else {
          showSnackbar({
            text: 'Invalid QR code. Please scan a Communiful profile QR.',
            type: 'error',
          });
        }
      }
    },
    [hasScanned, extractUserIdFromUrl, showSnackbar],
  );

  const handleRequestPermission = useCallback(async () => {
    const result = await requestPermission();
    if (!result.granted && !result.canAskAgain) {
      await Linking.openSettings();
    }
  }, [requestPermission]);

  // Reset scanned state when component becomes visible again
  useFocusEffect(
    useCallback(() => {
      setHasScanned(false);
    }, []),
  );

  if (!permission) {
    return (
      <View style={styles.container}>
        <TextReg style={styles.permissionText}>Loading camera...</TextReg>
      </View>
    );
  }

  if (!permission.granted) {
    return (
      <View style={styles.container}>
        <TextReg style={styles.permissionText}>
          Camera access is required to scan QR codes
        </TextReg>
        <Button
          variant={ButtonVariants.PURPLE}
          onPress={handleRequestPermission}
        >
          {permission.canAskAgain ? 'Grant Permission' : 'Open Settings'}
        </Button>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.cameraContainer}>
        <CameraView
          facing={cameraFacing}
          barcodeScannerSettings={{
            barcodeTypes: ['qr'],
          }}
          onBarcodeScanned={onBarcodeScanned}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.overlay}>
          <View style={styles.scanFrame} />
        </View>
        <IconButton
          icon={Camera02Icon}
          hugeIconProps={{ strokeWidth: 3 }}
          onPress={toggleCamera}
          backgroundColor={Colors.dark.purple0opacity50}
          style={styles.switchButton}
        />
      </View>
    </View>
  );
}

const createStyles = ({
  isMobile,
  screenWidth = 400,
}: WithResponsive<{ screenWidth: number }>) => {
  const containerPadding = isMobile ? 20 : 40;
  const maxCameraSize = Math.min(screenWidth - containerPadding * 2, 500);

  return StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: containerPadding,
      gap: 24,
    },
    cameraContainer: {
      width: maxCameraSize,
      aspectRatio: 1,
      borderRadius: 16,
      overflow: 'hidden',
      position: 'relative',
    },
    overlay: {
      ...StyleSheet.absoluteFillObject,
      padding: isMobile ? 16 : 24,
      justifyContent: 'center',
      alignItems: 'center',
    },
    scanFrame: {
      flex: 1,
      aspectRatio: 1,
      borderWidth: 2,
      borderColor: Colors.dark.yellow0,
      borderRadius: 12,
      backgroundColor: 'transparent',
    },
    switchButton: {
      position: 'absolute',
      bottom: 12,
      right: 12,
      borderWidth: 1,
    },
    permissionText: {
      fontSize: isMobile ? 14 : 16,
      textAlign: 'center',
      marginBottom: 16,
      opacity: 0.8,
    },
  });
};
